import React, {useContext, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';
import PropTypes from 'prop-types';

import {Button, Col, message, Modal, Row, Space, Table} from 'antd';
import {AiFillDelete, AiFillEdit, AiFillEye} from 'react-icons/all';
import {PatientContext} from '../../PatientContext';
import TreatmentEditor from './TreatmentEditor';
import TreatmentInfo from './TreatmentInfo';

import {findTreatments} from 'services/PatientService';
import {deleteTreatment} from 'services/TreatmentService';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';

const TreatmentList = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const {patientId} = props;

  const confirm = Modal.confirm;

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const patientContext = useContext(PatientContext);
  const {selectedPatient} = patientContext;

  const [treatmentId, setTreatmentId] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });

  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reloading, setReloading] = useState(false);

  const loadData = (params) => {
    const filters = params.filters
      ? Object.keys(params.filters).map((key) => ({
          fields: key,
          value: params.filters[key],
        }))
      : [];

    setLoading(true);
    findTreatments(headers, patientId || selectedPatient?.id, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setTreatments(res.data);
        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, [patientId]);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  const onAdd = () => {
    setTreatmentId(null);
    setEditing(true);
  };

  const onView = (id) => {
    setTreatmentId(id);
    setViewing(true);
  };

  const onEdit = (id) => {
    setTreatmentId(id);
    setEditing(true);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const showConfirm = (id) => {
    confirm({
      title: intl.messages['common.confirmDelete'],
      content: '',
      onOk() {
        deleteTreatment(headers, id)
          .then(() => {
            message.success(intl.messages['common.deleted']);
            setReloading(true);
          })
          .catch((error) => handleError(message, error));
      },
    });
  };

  const columns = [
    {
      title: intl.messages['treatment.name'],
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.messages['treatment.startTime'],
      dataIndex: 'period',
      key: 'period',
      render: (period) => <span>{period?.start}</span>,
    },
    {
      title: intl.messages['treatment.endTime'],
      dataIndex: 'period',
      key: 'period',
      render: (period) => <span>{period?.end}</span>,
    },
    {
      title: intl.messages['common.actions'],
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right',
      render: (id) => (
        <Space>
          <Button
            type='primary'
            shape='circle'
            ghost
            icon={<AiFillEye />}
            onClick={() => onView(id)}
          />
          <Button
            type='primary'
            shape='circle'
            ghost
            style={{borderColor: 'green'}}
            icon={<AiFillEdit color='green' />}
            onClick={() => onEdit(id)}
          />
          <Button
            danger
            shape='circle'
            icon={<AiFillDelete />}
            onClick={() => showConfirm(id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row>
        <Col span={24}>
          <Button type='primary' onClick={() => onAdd()}>
            {intl.messages['common.add']}
          </Button>
        </Col>
      </Row>
      <Table
        loading={loading}
        columns={columns}
        dataSource={treatments}
        rowKey={(record) => record.id}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />

      <TreatmentEditor
        patientId={patientId || selectedPatient?.id}
        treatmentId={treatmentId}
        editing={editing}
        setEditing={setEditing}
        setReloading={setReloading}
      />

      <TreatmentInfo
        treatmentId={treatmentId}
        viewing={viewing}
        setViewing={setViewing}
        setReloading={setReloading}
      />
    </>
  );
};

export default TreatmentList;

TreatmentList.propTypes = {
  patientId: PropTypes.string,
};
